import type Phaser from 'phaser';
import type { Phase, PointNumber, RollResult } from '@casino/craps-engine';

/**
 * Casino-stickman call for a given two-dice result.
 * - Uses common vernacular for the 2/3/11/12 corners and the hardways.
 * - Plain numerals for 5 and 9 (no widely-recognised single-word call).
 *
 * `context` adds the phase-specific suffix the stickman would call: "CRAPS"
 * on come-out 2/3/12, "WINNER" on come-out 7/11 or a point made, "SEVEN OUT"
 * when 7 hits during the point round. Without context the bare nickname is
 * returned (kept so callers without phase knowledge still get a sensible
 * label).
 */
export function rollLabel(
  roll: RollResult,
  context?: { phase: Phase; point?: PointNumber },
): string {
  const { d1, d2, total } = roll;
  let nickname: string;
  if (total === 2) nickname = 'ACES';
  else if (total === 3) nickname = 'ACE-DEUCE';
  else if (total === 12) nickname = 'BOXCARS';
  else if (total === 11) nickname = 'YO';
  else if (total === 7) nickname = 'SEVEN';
  else if (total === 5) nickname = 'FIVE';
  else if (total === 9) nickname = 'NINE';
  else if (total === 4 || total === 6 || total === 8 || total === 10) {
    nickname = d1 === d2 ? `HARD ${total}` : `EASY ${total}`;
  } else {
    nickname = String(total);
  }

  if (!context) return nickname;
  if (context.phase === 'COME_OUT') {
    if (total === 2 || total === 3 || total === 12) return `${nickname} · CRAPS`;
    if (total === 7 || total === 11) return `${nickname} · WINNER`;
    return nickname;
  }
  // POINT phase: 7 ends the round; rolling the point itself wins the line.
  if (total === 7) return 'SEVEN OUT';
  if (context.point !== undefined && total === context.point) return `${nickname} · WINNER`;
  return nickname;
}

/**
 * Flash a stickman-call label in a fixed spot near the top of the screen,
 * just below the bankroll bezel.
 *
 * Position is derived from canvas width (centered horizontally) and a
 * dpr-scaled vertical offset that places the pill below the money tray —
 * predictable across rolls regardless of where the dice settled.
 */
export function flashRollLabel(scene: Phaser.Scene, text: string): Promise<void> {
  const dpr = (scene.game.registry.get('dpr') as number | undefined) ?? 1;
  const px = (n: number) => Math.round(n * dpr);
  const cx = scene.scale.width / 2;
  // Money bezel: outerY=0, height=56*dpr, concave dip ~0.35 → reaches
  // ~76*dpr at its midpoint. Place the roll pill comfortably below that.
  const cy = px(110);

  const label = scene.add.text(0, 0, text, {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: `${px(40)}px`,
    color: '#f4d35e',
    fontStyle: 'bold',
  }).setOrigin(0.5);

  const padX = px(18);
  const padY = px(9);
  const radius = px(12);
  const w = label.width + padX * 2;
  const h = label.height + padY * 2;

  const bg = scene.add.graphics();
  // Fully opaque fill so the pill reads cleanly on launch — fade is handled
  // by the tween below.
  bg.fillStyle(0x000000, 1);
  bg.fillRoundedRect(-w / 2, -h / 2, w, h, radius);
  bg.lineStyle(px(1.5), 0xd4af37, 1);
  bg.strokeRoundedRect(-w / 2, -h / 2, w, h, radius);

  const container = scene.add.container(cx, cy, [bg, label]);
  container.setDepth(900);

  // Hold fully opaque for a beat, then fade out while drifting upward.
  // Returns a Promise that resolves when the pill has fully faded, so
  // callers can gate downstream UI on "the screen has cleared" rather
  // than guessing with a fixed delay (see crapsBridge bust-modal flow).
  return new Promise<void>((resolve) => {
    scene.tweens.add({
      targets: container,
      y: container.y - px(10),
      alpha: { from: 1, to: 0 },
      duration: 1800,
      delay: 600,
      ease: 'Cubic.Out',
      onComplete: () => {
        container.destroy();
        resolve();
      },
    });
  });
}
